import { defineStore } from 'pinia';
import isEqual from 'lodash/isEqual';
import { formatMediaListData } from '@/utils';
import { LibraryAPI } from '@/apis';

// TODO(gdiFindMediaSelectMedia): Can remove more of this code once GDI Select Media is finished.
// Remaining usages before we can remove:
//   - FindMedia/SearchBar
//   - MediaPopup (similar media/media sources)
//   - Dashboards (competitors)
export const useSearchStore = defineStore('search', {
  resetOnBrandChange: true,
  state: () => ({
    pending: {
      similarMediaList: false,
      mediaSourceList: false,
    },
    mediaSearch: false,
    imageFile: null,
    similarMediaList: [],
    mediaSourceList: [],
    uniqueSimilarImageGroupIds: [],
    competitors: {},
    competitorsRequestStatus: null,
  }),
  getters: {
    searchImageFile() {
      return this.imageFile;
    },
    formattedSimilarMediaList() {
      return formatMediaListData(this.similarMediaList);
    },
    filterCompetitors() {
      return ({ sourceAccountIds = [], source = '' } = {}) =>
        sourceAccountIds?.map((sourceAccountId) => {
          const competitorData = this.competitors[source]?.find((competitor) =>
            isEqual(competitor.sourceAccountId, sourceAccountId),
          );
          return competitorData ?? { sourceAccountId, missing: true };
        }) ?? [];
    },
  },
  actions: {
    async getSimilarMediaList({ brandId, mediaId, groupId, sources, limit }) {
      this.pending.similarMediaList = true;
      try {
        const response = await LibraryAPI.getSimilarMediaList({
          brandId,
          mediaId,
          groupId,
          sources,
          limit,
        });

        // no paging, so reset on every request.
        this.uniqueSimilarImageGroupIds = [];
        // exclude self
        if (groupId) {
          this.uniqueSimilarImageGroupIds = [groupId.toString()];
        }
        // make sure only one media is displayed for each image group
        this.similarMediaList = response.data
          .map((media) => {
            const mediaGroup = media?.mediaGroup?.toString();
            if (!!mediaGroup && this.uniqueSimilarImageGroupIds.indexOf(mediaGroup) < 0) {
              this.uniqueSimilarImageGroupIds.push(mediaGroup);
              return media;
            }
            return null;
          })
          .filter((item) => item !== null);
      } catch {
        this.uniqueSimilarImageGroupIds = [];
        this.similarMediaList = [];
      } finally {
        this.pending.similarMediaList = false;
      }
    },
    async getMediaSourceList({ brandId, groupId, globalIds }) {
      this.pending.mediaSourceList = true;
      try {
        const response = await LibraryAPI.getMediaSourceList({ brandId, groupId, globalIds });
        this.mediaSourceList = response;
      } finally {
        this.pending.mediaSourceList = false;
      }
    },
    setMediaSearchStatus({ status }) {
      this.mediaSearch = status;
    },
    clearMediaSearchList() {
      this.searchListFullyLoaded = false;
      this.searchPage = 1;
      this.mediaSearchList = [];
      this.uniqueImageGroupIds = {};
    },
    clearMediaSourceList() {
      this.mediaSourceList = [];
    },
    async getCompetitors(source, brandIds) {
      this.competitorsRequestStatus = 'pending';

      const res = await LibraryAPI.apiGetCompetitors({ payload: { source, brandIds } });
      const competitors = res?.data || [];
      const cleanedCompetitors = competitors.filter(
        (competitor) => competitor.handle && competitor.avatarUrl && competitor.sourceAccountId,
      );
      this.competitors = { ...this.competitors, [source]: cleanedCompetitors };
      this.competitorsRequestStatus = 'success';
    },
    getCompetitorNames(sourceAccountIds, source) {
      return this.filterCompetitors({
        sourceAccountIds,
        source,
      })
        .filter((competitor) => !competitor.missing)
        .map((competitor) => competitor.handle);
    },
  },
});
